// hooks/useClusterer.js
import { useEffect, useRef, useCallback } from "react";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { createPortal } from "react-dom";
import { ClusterMarker } from "../../components/maps/markers/ClusterMarker";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { globalConfig } from "../../config";

const DEBOUNCE_MS = 50;

/**
 * Initialises the MarkerClusterer with a custom renderer.
 * - Custom renderer uses AdvancedMarkerElements so React controls content
 * - clusteringend builds portals with true item counts
 * - Exposes scheduleRender() for the marker sync hook to call
 *
 * @param {google.maps.Map|null}   map
 * @param {Function}               registerPortal
 * @param {Function}               unregisterPortal
 * @param {string|null}            selectedId
 * @param {Function}               setSelectedId
 * @returns {{ clustererRef, scheduleRender }}
 */
export function useClusterer(
  map,
  registerPortal,
  unregisterPortal,
  selectedId,
  setSelectedId,
  onClustererRenderComplete
) {
  const mapLibMarker = useMapsLibrary("marker");
  const clustererRef = useRef(null);
  const clusterMarkerMapRef = useRef(new Map()); // clusterId -> { marker, element }
  const debounceTimerRef = useRef(null);
  const isClusteringRef = useRef(false);

  // Keep selectedId fresh inside callbacks without re-running effects
  const selectedIdRef = useRef(selectedId);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const setSelectedIdRef = useRef(setSelectedId);
  useEffect(() => {
    setSelectedIdRef.current = setSelectedId;
  }, [setSelectedId]);

  useEffect(() => {
    if (!map) return;
    if (!mapLibMarker) return;

    // --- Custom renderer ---
    // Google calls render() for each cluster — we return an AdvancedMarkerElement
    // whose content element React will portal into
    const renderer = {
      render({ count, position, markers }, stats, map) {
        // Reuse existing cluster marker element if available

        const clusterId = "cluster-" + markers[0].__itemId.toString();

        var entry = clusterMarkerMapRef.current.get(clusterId);

        if (!entry) {
          const element = document.createElement("div");
          const marker = new mapLibMarker.AdvancedMarkerElement({
            position,
            content: element,
          });
          entry = { marker, element };
          clusterMarkerMapRef.current.set(clusterId, entry);
        } else {
          // Reuse the existing entry, but recreate the marker
          entry.marker.map = null;
          entry.marker = null;
          entry.marker = new mapLibMarker.AdvancedMarkerElement({
            position,
            content: entry.element,
          });
        }

        return entry.marker;
      },
    };

    const clusterer = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({
        radius: globalConfig.assetClusterRadius,
        maxZoom: 15,
      }),
      renderer,
      onClusterClick(e, cluster) {
        // map.setZoom(Math.min(16, map.getZoom() + 4.0));
        // cluster.markers[0].position.lat
        // Zoom to fit the cluster's markers

        const bounds = new window.google.maps.LatLngBounds();
        cluster.markers.forEach((m) => bounds.extend(m.position));
        map.fitBounds(bounds, 128);

        // console.log("cluster click ", { cluster: cluster });
        // map.setZoom(18);
        // map.panTo(cluster.marker.position);
      },
    });

    // --- clusteringbegin ---
    clusterer.addListener("clusteringbegin", () => {
      isClusteringRef.current = true;
    });

    // --- clusteringend ---
    clusterer.addListener("clusteringend", (c) => {
      isClusteringRef.current = false;
      const activeClusters = new Set();

      if (c.clusters) {
        c.clusters.forEach((cluster) => {
          if (cluster.markers.length < 2) return;

          cluster.marker.position = cluster.position;

          // True count — sum of each group marker's __count
          const trueCount = cluster.markers.reduce((sum, marker) => {
            return sum + (marker.__count ?? 1);
          }, 0);

          // const position = cluster.position ?? cluster.marker?.position;
          const clusterId = "cluster-" + cluster.markers[0].__itemId.toString();
          activeClusters.add(clusterId);

          var entry = clusterMarkerMapRef.current.get(clusterId);
          if (!entry) return;

          registerPortal(
            clusterId,
            entry.element,
            <ClusterMarker
              count={trueCount}
              isSelected={selectedIdRef.current === clusterId}
              // onClick={() => {
              //   // Zoom to fit the cluster's markers
              //   const bounds = new window.google.maps.LatLngBounds();
              //   cluster.markers.forEach((m) => bounds.extend(m.position));
              //   map.fitBounds(bounds);
              //   setSelectedIdRef.current(clusterId);
              // }}
            />
          );
        });
      }

      // Clean up cluster markers that are no longer active
      clusterMarkerMapRef.current.forEach((entry, clusterId) => {
        if (!activeClusters.has(clusterId)) {
          unregisterPortal(clusterId);
          entry.marker.setMap(null);
          entry.marker = null;
          clusterMarkerMapRef.current.delete(clusterId);
        }
      });

      onClustererRenderComplete();
    });

    clustererRef.current = clusterer;

    return () => {
      clusterer.setMap(null);
      clustererRef.current = null;
    };
  }, [map, mapLibMarker]);

  // Debounced render — safe to call many times in quick succession
  const scheduleRender = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (clustererRef.current && !isClusteringRef.current) {
        clustererRef.current.render();
      }
      debounceTimerRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  return { clustererRef, scheduleRender };
}
